import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { resolveAnswerArticleLines } from "@/features/timesheetFlow/billingAnswerDisplay";
import { formatCurrency, positionEpNetto } from "@/lib/documentCalc";
import { readGdMeta } from "@/pages/dokumente/gdPositionMeta";
import { patchLeistungVkManuell } from "@/pages/dokumente/documentLeistungPricing";
import {
  calcMergedChildrenSum,
  mergedChildToEditorPosition,
  newLocalId,
  normalizePositionTyp,
  patchPositionMeta,
  positionTotals,
  reconcileMergedPositionFromChildren,
  snapshotMergedChild,
  syncLeistungEinzelpreis,
  type EditorPosition,
  type LeistungKalkulation,
} from "@/pages/dokumente/documentPositionModel";

export type BillingAnswerArticleDisplayLine = {
  key: string;
  label: string;
  epLabel?: string | null;
  totalLabel?: string | null;
  isChild?: boolean;
};

export function formatEditorPositionLine(position: EditorPosition): string {
  const typ = String(position.position_typ || "").toLowerCase();
  const label = typ === "leistung" ? "Leistung" : typ === "artikel" ? "Artikel" : "Position";
  const name = String(position.bezeichnung || "").trim() || label;
  const menge = Number(position.menge);
  const einheit = String(position.einheit || "").trim();
  const qty =
    Number.isFinite(menge) && menge > 0
      ? `${String(menge).replace(".", ",")}${einheit ? ` ${einheit}` : ""}`
      : "";
  return qty ? `${qty} × ${name}` : name;
}

/** Dokumentähnliche Zeile inkl. EP / Gesamt (wenn Preise sichtbar). */
export function formatEditorPositionLineWithPrice(
  position: EditorPosition,
  options?: { showPrices?: boolean; currency?: string },
): string {
  const base = formatEditorPositionLine(position);
  if (!options?.showPrices) return base;
  const synced =
    normalizePositionTyp(position.position_typ) === "leistung"
      ? syncLeistungEinzelpreis(position)
      : position;
  const ep = positionEpNetto(synced);
  const total = positionTotals(synced).zeilen_netto;
  const currency = options.currency || "EUR";
  return `${base} · EP ${formatCurrency(ep, currency)} · ${formatCurrency(total, currency)}`;
}

export function editorPositionToMaterial(position: EditorPosition): TimesheetMaterialRecord {
  const synced =
    String(position.position_typ || "").toLowerCase() === "leistung"
      ? syncLeistungEinzelpreis(position)
      : position;
  const typ = String(synced.position_typ || "").toLowerCase();
  return {
    menge: Number(synced.menge) > 0 ? Number(synced.menge) : 1,
    einheit:
      String(synced.einheit || "").trim() ||
      (typ === "leistung" ? "pauschal" : "Stk"),
    text: String(synced.bezeichnung || "").trim() || formatEditorPositionLine(synced),
    artikel_id: synced.artikel_id ?? null,
    hero_nr: synced.hero_nr ?? null,
    source_type: synced.artikel_id != null ? "hero" : "manual",
    gd_position_id: synced.quelle_position_id ?? null,
  };
}

export function editorPositionPricingPayload(position: EditorPosition): {
  einzelpreis_netto: number;
  kalkulation_json: Record<string, unknown> | null;
  text: string;
  einheit: string | null;
} {
  const synced =
    String(position.position_typ || "").toLowerCase() === "leistung"
      ? syncLeistungEinzelpreis(position)
      : position;
  const kalk =
    synced.kalkulation_json && typeof synced.kalkulation_json === "object"
      ? (synced.kalkulation_json as Record<string, unknown>)
      : null;
  return {
    einzelpreis_netto: Number(synced.einzelpreis_netto) || 0,
    kalkulation_json: kalk,
    text: String(synced.bezeichnung || "").trim() || formatEditorPositionLine(synced),
    einheit: String(synced.einheit || "").trim() || null,
  };
}

/** Materialzeile → EditorPosition (ohne merged_children am Container). */
export function materialToEditorPosition(
  material: TimesheetMaterialRecord,
  index = 0,
): EditorPosition {
  const hasPricing =
    material.einzelpreis_netto != null ||
    (material.kalkulation_json != null && typeof material.kalkulation_json === "object");
  const typ: "artikel" | "leistung" =
    hasPricing || !material.artikel_id ? "leistung" : "artikel";

  let kalkulation_json: EditorPosition["kalkulation_json"] = undefined;
  if (material.kalkulation_json && typeof material.kalkulation_json === "object") {
    const raw = { ...(material.kalkulation_json as Record<string, unknown>) };
    const meta = readGdMeta(raw);
    // Beim Hydratisieren einzelner Kinder den Container-Meta nicht mitnehmen.
    if (meta.merged_children?.length) {
      const gdMeta = { ...(raw._gd_meta as Record<string, unknown>) };
      delete gdMeta.merged_children;
      if (Object.keys(gdMeta).length) raw._gd_meta = gdMeta;
      else delete raw._gd_meta;
    }
    kalkulation_json = raw as EditorPosition["kalkulation_json"];
  }

  const row: EditorPosition = {
    localId: material.id != null ? `mat-${material.id}` : newLocalId("mat"),
    sort_index: index,
    position_typ: typ,
    bezeichnung: String(material.text || "").trim() || "Position",
    beschreibung: material.bemerkung ?? undefined,
    menge: Number(material.menge) > 0 ? Number(material.menge) : 1,
    einheit: String(material.einheit || "").trim() || (typ === "leistung" ? "pauschal" : "Stk"),
    einzelpreis_netto: Number(material.einzelpreis_netto) || 0,
    rabatt_prozent: 0,
    steuersatz_prozent: 19,
    artikel_id: material.artikel_id ?? null,
    hero_nr: material.hero_nr ?? null,
    quelle_position_id: material.gd_position_id ?? null,
    kalkulation_json:
      typ === "leistung"
        ? (kalkulation_json as LeistungKalkulation) || {
            artikel_liste: [],
            lohn_liste: [],
            vk_driver: "manuell",
          }
        : kalkulation_json,
  };
  return typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
}

export function getMaterialMergedChildren(
  material: TimesheetMaterialRecord | null | undefined,
): EditorPosition[] {
  if (!material?.kalkulation_json) return [];
  const children = readGdMeta(material.kalkulation_json).merged_children;
  if (!Array.isArray(children) || children.length === 0) return [];
  return children.map((child) => mergedChildToEditorPosition(child));
}

/** Draft-Positionen zu einer zusammengefassten Leistung mit internen Unterpositionen. */
export function buildMergedLeistungFromPositions(
  positions: EditorPosition[],
  parentLabel?: string | null,
): EditorPosition {
  const children = positions.map((row) => snapshotMergedChild(row));
  const totalNetto = calcMergedChildrenSum(children);
  const title =
    String(parentLabel || "").trim() ||
    positions
      .map((row) => String(row.bezeichnung || "").trim())
      .filter(Boolean)
      .join(" · ") ||
    "Zusammengefasste Position";

  let merged: EditorPosition = {
    localId: newLocalId("merged"),
    sort_index: 0,
    position_typ: "leistung",
    bezeichnung: title,
    menge: 1,
    einheit: "pauschal",
    einzelpreis_netto: totalNetto,
    rabatt_prozent: 0,
    steuersatz_prozent: positions[0]?.steuersatz_prozent ?? 19,
    kalkulation_json: {
      artikel_liste: [],
      lohn_liste: [],
      vk_driver: "manuell",
    },
  };
  merged = patchPositionMeta(merged, { merged_children: children, preisart: "festpreis" });
  merged = reconcileMergedPositionFromChildren(merged);
  const kalk = (merged.kalkulation_json || {
    artikel_liste: [],
    lohn_liste: [],
  }) as LeistungKalkulation;
  merged = syncLeistungEinzelpreis({
    ...merged,
    einzelpreis_netto: totalNetto,
    kalkulation_json: patchLeistungVkManuell(kalk, totalNetto),
  });
  return merged;
}

export function resolveAnswerAppendedMaterialIds(input: {
  respondedAt?: string | null;
  actionLog: Array<{ action?: string; created_at?: string; details?: unknown }>;
}): number[] {
  if (!input.respondedAt) return [];
  const responseTime = new Date(input.respondedAt).getTime();
  if (Number.isNaN(responseTime)) return [];

  const windowMs = 10 * 60 * 1000;
  const materialIds = new Set<number>();

  for (const entry of input.actionLog) {
    if (entry.action !== "material_appended") continue;
    const entryTime = new Date(entry.created_at || "").getTime();
    if (Number.isNaN(entryTime) || Math.abs(entryTime - responseTime) > windowMs) continue;

    const details =
      entry.details && typeof entry.details === "object"
        ? (entry.details as Record<string, unknown>)
        : null;
    let parsed = details;
    if (!parsed && typeof entry.details === "string") {
      try {
        const value = JSON.parse(entry.details) as unknown;
        parsed =
          typeof value === "object" && value != null ? (value as Record<string, unknown>) : null;
      } catch {
        parsed = null;
      }
    }
    const ids = parsed?.material_position_ids;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      const num = Number(id);
      if (Number.isFinite(num)) materialIds.add(num);
    }
  }

  return [...materialIds];
}

/** Positionen für „Antwort bearbeiten“ aus Merge-Meta oder Materialien rekonstruieren. */
export function hydrateAnswerPositions(input: {
  materialPositionId?: number | null;
  respondedAt?: string | null;
  materials: TimesheetMaterialRecord[];
  actionLog: Array<{ action?: string; created_at?: string; details?: unknown }>;
}): EditorPosition[] {
  const linkedId = input.materialPositionId ?? null;
  const linked =
    linkedId != null
      ? input.materials.find((material) => Number(material.id) === Number(linkedId))
      : undefined;

  const fromMerge = getMaterialMergedChildren(linked);
  if (fromMerge.length > 0) return fromMerge;

  const appendedIds = new Set(
    resolveAnswerAppendedMaterialIds({
      respondedAt: input.respondedAt,
      actionLog: input.actionLog,
    }),
  );

  // Allgemeine Antwort: Merge-Container unter den angehängten Materialien.
  if (linkedId == null) {
    const appended = input.materials.filter(
      (material) => material.id != null && appendedIds.has(Number(material.id)),
    );
    if (appended.length === 1) {
      const only = appended[0]!;
      const children = getMaterialMergedChildren(only);
      if (children.length > 0) return children;
      return [materialToEditorPosition(only, 0)];
    }
    if (appended.length > 1) {
      return appended.map((material, index) => materialToEditorPosition(material, index));
    }
    return [];
  }

  const positions: EditorPosition[] = [];
  if (linked) {
    positions.push(materialToEditorPosition(linked, 0));
  }
  for (const material of input.materials) {
    if (material.id == null || !appendedIds.has(Number(material.id))) continue;
    if (linkedId != null && Number(material.id) === Number(linkedId)) continue;
    positions.push(materialToEditorPosition(material, positions.length));
  }
  return positions;
}

/** Material → EditorPosition für Rechnungsvorschau (inkl. merged_children am Container). */
export function materialToInvoiceEditorPosition(
  material: TimesheetMaterialRecord,
  index: number,
): EditorPosition {
  const hasPricing =
    material.einzelpreis_netto != null ||
    (material.kalkulation_json != null && typeof material.kalkulation_json === "object");
  const typ: "artikel" | "leistung" =
    hasPricing || !material.artikel_id ? "leistung" : "artikel";

  const baseKalk =
    material.kalkulation_json && typeof material.kalkulation_json === "object"
      ? (material.kalkulation_json as EditorPosition["kalkulation_json"])
      : typ === "leistung"
        ? { artikel_liste: [], lohn_liste: [], vk_driver: "manuell" as const }
        : undefined;
  const stampedKalk =
    material.id != null
      ? patchPositionMeta(
          { kalkulation_json: baseKalk } as EditorPosition,
          { timesheet_material_position_id: Number(material.id) },
        ).kalkulation_json
      : baseKalk;

  const row: EditorPosition = {
    localId: material.id != null ? `inv-mat-${material.id}` : newLocalId("inv"),
    sort_index: index,
    position_typ: typ,
    bezeichnung: String(material.text || "").trim() || "Position",
    beschreibung: material.bemerkung ?? undefined,
    menge: Number(material.menge) > 0 ? Number(material.menge) : 1,
    einheit: String(material.einheit || "").trim() || (typ === "leistung" ? "pauschal" : "Stk"),
    einzelpreis_netto: Number(material.einzelpreis_netto) || 0,
    rabatt_prozent: 0,
    steuersatz_prozent: 19,
    artikel_id: material.artikel_id ?? null,
    hero_nr: material.hero_nr ?? null,
    quelle_position_id: material.gd_position_id ?? null,
    kalkulation_json: stampedKalk,
  };
  return typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
}

export function buildInvoicePreviewPositionsFromMaterials(
  materials: TimesheetMaterialRecord[],
  options?: { excludeMaterialIds?: Iterable<number> },
): EditorPosition[] {
  const exclude = new Set(
    [...(options?.excludeMaterialIds ?? [])].map((id) => Number(id)).filter(Number.isFinite),
  );
  return materials
    .filter((material) => material.id == null || !exclude.has(Number(material.id)))
    .map((material, index) => materialToInvoiceEditorPosition(material, index));
}

function mapChildrenToDisplayLines(
  children: EditorPosition[],
  options?: { showPrices?: boolean; keyPrefix?: string },
): BillingAnswerArticleDisplayLine[] {
  const showPrices = options?.showPrices !== false;
  const keyPrefix = options?.keyPrefix || "child";
  return children.map((position, index) => {
    const synced =
      normalizePositionTyp(position.position_typ) === "leistung"
        ? syncLeistungEinzelpreis(position)
        : position;
    return {
      key: position.localId || `${keyPrefix}-${index}`,
      label: formatEditorPositionLine(synced),
      epLabel: showPrices ? formatCurrency(positionEpNetto(synced)) : null,
      totalLabel: showPrices ? formatCurrency(positionTotals(synced).zeilen_netto) : null,
      isChild: true,
    };
  });
}

/** Anzeigezeilen + Unterpositions-Zähler für beantwortete Rückfragen. */
export function resolveAnsweredArticleDisplay(input: {
  materialPositionId?: number | null;
  answerText?: string | null;
  respondedAt?: string | null;
  materials: TimesheetMaterialRecord[];
  actionLog: Array<{ action?: string; created_at?: string; details?: unknown }>;
  showPrices?: boolean;
  formatMaterialLine: (material: {
    text?: string | null;
    menge?: number | null;
    einheit?: string | null;
    hero_nr?: string | null;
  }) => string | null;
}): { lines: BillingAnswerArticleDisplayLine[]; unterpositionCount: number } {
  const showPrices = input.showPrices !== false;
  const linked =
    input.materialPositionId != null
      ? input.materials.find(
          (material) => Number(material.id) === Number(input.materialPositionId),
        )
      : undefined;

  const mergedChildren = getMaterialMergedChildren(linked);
  if (mergedChildren.length > 0) {
    return {
      unterpositionCount: mergedChildren.length,
      lines: mapChildrenToDisplayLines(mergedChildren, { showPrices }),
    };
  }

  if (input.materialPositionId == null) {
    const appendedIds = resolveAnswerAppendedMaterialIds({
      respondedAt: input.respondedAt,
      actionLog: input.actionLog,
    });
    const appended = input.materials.filter(
      (material) => material.id != null && appendedIds.includes(Number(material.id)),
    );
    if (appended.length === 1) {
      const children = getMaterialMergedChildren(appended[0]!);
      if (children.length > 0) {
        return {
          unterpositionCount: children.length,
          lines: mapChildrenToDisplayLines(children, {
            showPrices,
            keyPrefix: "gen-child",
          }),
        };
      }
    }
  }

  const fallback = resolveAnswerArticleLines({
    answerText: input.answerText,
    respondedAt: input.respondedAt,
    materials: input.materials,
    actionLog: input.actionLog,
    formatMaterialLine: input.formatMaterialLine,
  });
  return {
    unterpositionCount: 0,
    lines: fallback.map((label, index) => ({ key: `${label}-${index}`, label })),
  };
}
